import type { LocalDatabaseProduct } from "../../../Data/_Bases/BaseProductRepository";
import type {
    LocalDatabasePromotion,
    LocalDatabasePromotionCriteria,
} from "../../../Data/_Bases/BasePromotionRepository";
import Cloner from "../../Generic/Cloner";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { AdvancedPromotionCache } from "./AdvancedPromotionCache";
import { Knapsack } from "./Knapsack";
import { CalculationException } from "./PromotionErrors";
import { type ComparisonProduct, PromotionManager } from "./PromotionManager";
import type {
    PromotionCalculationProduct,
    PromotionCalculationProductCount,
    PromotionCalculationProductGroup,
    PromotionCalculationPromotion,
    PromotionCalculationPromotionCriteria,
    PromotionCalculationResult,
    PromotionCalculationResultBranch,
    PromotionCalculationResultSingle,
    PromotionManagerProduct,
    PromotionManagerSettings,
} from "./PromotionTypes";
import { isSpecialProduct, type SalePromotionProductPrice } from "./PromotionUtilities";

const MAX_BRANCH_DEPTH = 30;

export class PromotionCalculation {
    protected manager: PromotionManager;
    protected promotion: PromotionCalculationPromotion;
    protected products: Array<PromotionManagerProduct>;
    protected settings: PromotionManagerSettings;
    protected optionalOnly: boolean;
    protected requiredOnly: boolean;
    protected criteriaMet: number;
    protected maxMultiplication = Infinity;
    protected applicablePromotions: Array<string>;
    protected groupedProducts: Record<string, PromotionCalculationProductGroup> = {};
    protected promotionIsInvalid = false;

    protected shouldCancel: () => boolean;

    constructor(
        manager: PromotionManager,
        promotion: LocalDatabasePromotion,
        products: Array<PromotionManagerProduct>,
        useCrossPromotionCount: boolean,
        shouldCancel: () => boolean,
        applicablePromotions: Array<string>,
        protected productCache: Record<string, LocalDatabaseProduct>,
        protected globalProducts: Array<ComparisonProduct>
    ) {
        this.manager = manager;
        this.products = products;
        this.promotion = Cloner.Clone(promotion);
        this.applicablePromotions = applicablePromotions;

        this.settings = {
            ...this.manager.settings,
            cross_promotion_count: useCrossPromotionCount,
        };

        this.optionalOnly = true;
        this.requiredOnly = true;
        this.criteriaMet = 0;

        this.shouldCancel = shouldCancel;
    }

    /**
     * Check if the promotion is in the provided criteria
     */
    public static isPromotionInCriteria(
        product: LocalDatabaseProduct,
        criteria: LocalDatabasePromotionCriteria
    ): "ignore" | "include" | "exclude" {
        // Check if the product should be included by including the product directly
        let included = false;

        for(let k = 0, c = criteria.products.length; k < c; k++) {
            if(criteria.products[k].uuid === product.uuid) {
                included = true;
                break;
            }
        }

        // Check if the product should be excluded
        let exclude = false;

        if(!exclude && !included) {
            if(product.brand_id && criteria.excludes.brands.includes(product.brand_id)) {
                exclude = true;
            }
        }

        if(!exclude && !included) {
            if(product.category_id && criteria.excludes.categories.includes(product.category_id)) {
                exclude = true;
            }
        }

        if(!exclude && !included) {
            if(product.family_id && criteria.excludes.families.includes(product.family_id)) {
                exclude = true;
            }
        }

        if(!exclude && !included) {
            if(criteria.excludes.products.includes(product.uuid)) {
                exclude = true;
            }
        }

        if(!exclude && !included) {
            for(let k = 0, c = product.tags.length; k < c; k++) {
                if(criteria.excludes.tags.includes(product.tags[k])) {
                    exclude = true;
                    break;
                }
            }
        }

        if(exclude) {
            // Check if we should count the quantity towards the promotion anyway
            return "exclude";
        }

        // Check if the criteria is counting all items
        if(typeof criteria.allItems === "number") {
            return "include";
        }

        // Check if there are any secondary ways the product should be included
        if(!included) {
            for(let k = 0, c = criteria.brands.length; k < c; k++) {
                if(criteria.brands[k].uuid === product.brand_id) {
                    included = true;
                    break;
                }
            }
        }

        if(!included) {
            for(let k = 0, c = criteria.categories.length; k < c; k++) {
                if(criteria.categories[k].uuid === product.category_id) {
                    included = true;
                    break;
                }
            }
        }

        if(!included) {
            for(let k = 0, c = criteria.families.length; k < c; k++) {
                if(criteria.families[k].uuid === product.family_id) {
                    included = true;
                    break;
                }
            }
        }

        if(!included) {
            for(let k = 0, c = criteria.tags.length; k < c; k++) {
                if(included) break;

                for(let x = 0, m = product.tags.length; x < m; x++) {
                    if(criteria.tags[k].uuid === product.tags[x]) {
                        included = true;
                        break;
                    }
                }
            }
        }

        if(!included) return "ignore";

        return "include";
    }

    /**
     * Determine if the product belongs in the criteria
     * @param product
     * @param criteria
     * @param index
     * @returns {Promise<string>}
     * @private
     */
    protected checkIfProductInCriteria(
        product: PromotionManagerProduct | ComparisonProduct,
        criteria: LocalDatabasePromotionCriteria,
        index: number
    ): "ignore" | "include" | "exclude" {
        // Check if it's in the cache
        const inCriteria = this.manager.cache.checkCriteriaCache(product.uuid, index, this.promotion.uuid);

        if(inCriteria) {
            return inCriteria;
        }

        // Get the repository product
        const dbProduct = this.productCache[product.uuid];

        // Go through the promotion criteria and determine if the product exists within it
        return PromotionCalculation.isPromotionInCriteria(dbProduct, criteria);
    }

    /**
     * Get the products for each criteria in the provided promotion
     */
    protected getPromotionProducts(
        products: Array<PromotionManagerProduct>
    ): {
        promoProducts: Array<Record<string, PromotionCalculationProduct>>;
        productQuantity: number;
        basePrice: number;
        affectedProducts: Array<string>;
    } {
        const promoProducts = ArrayPool.Get<Record<string, PromotionCalculationProduct>>();

        const affectedProducts = ArrayPool.Get<string>();

        let productQuantity = 0;
        let basePrice = 0;

        const productQuantities = ObjectPool.Get() as Record<string, {
            finished: boolean;
        }>;

        const groupedProducts = ObjectPool.Get() as Record<string, PromotionCalculationProductGroup>;

        let hasUnfinished = false;

        // Loop through the criteria
        for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
            // Add the criteria line to the promo products
            promoProducts[i] = ObjectPool.Get() as {};
            const productCounts = ObjectPool.Get() as Record<string, PromotionCalculationProductCount>;

            let priceField: "everyday" | "base" = "everyday";

            if(!this.promotion.criteria[i].discount_everyday_promotions) {
                priceField = "base";
            }

            // Set product quantities to finished
            if(hasUnfinished) {
                for(const j in productQuantities) {
                    if(!Object.prototype.hasOwnProperty.call(productQuantities, j)) continue;

                    productQuantities[j].finished = true;

                    if(typeof groupedProducts[j] !== "undefined") {
                        groupedProducts[j].finished = true;
                    }
                }

                hasUnfinished = false;
            }

            // Loop through the products provided
            for(let j = 0, n = products.length; j < n; j++) {
                if(products[j].special) continue;

                const quantity = products[j].quantity;

                if(quantity <= 0) {
                    continue;
                }

                // Check if the product has already been included
                if(typeof promoProducts[i][products[j].uuid] !== "undefined") {
                    // Add quantity to the existing product
                    promoProducts[i][products[j].uuid].quantity += quantity;

                    // Add the price to the existing product
                    promoProducts[i][products[j].uuid].price += products[j][priceField];

                    // Add the base price
                    promoProducts[i][products[j].uuid].base += products[j].everyday;

                    // Add to the product counts
                    productCounts[products[j].uuid].criteria_quantity += products[j].quantity;
                    productCounts[products[j].uuid].criteria_price += products[j][priceField];

                    // Add to the grouped products
                    if(!groupedProducts[products[j].uuid].finished) {
                        groupedProducts[products[j].uuid].quantity += products[j].quantity;
                        groupedProducts[products[j].uuid].base += products[j].everyday;
                    }

                    // Add to product quantities if we're not using cross promotion count
                    if(!this.settings.cross_promotion_count) {
                        if(!productQuantities[products[j].uuid].finished) {
                            productQuantity += products[j].quantity;
                            basePrice += products[j].everyday;
                        }
                    }

                    continue;
                }

                // Check if the product is in the criteria
                const status = this.checkIfProductInCriteria(products[j], this.promotion.criteria[i], i);

                // Apply the status
                if(status === "ignore") continue;

                if(status === "include") {
                    promoProducts[i][products[j].uuid] = ObjectPool.Get() as PromotionCalculationProduct;
                    promoProducts[i][products[j].uuid].quantity = quantity;
                    promoProducts[i][products[j].uuid].price = products[j][priceField];
                    promoProducts[i][products[j].uuid].base = products[j].everyday;
                    promoProducts[i][products[j].uuid].adjustable = true;
                    promoProducts[i][products[j].uuid].uuid = products[j].uuid;
                    promoProducts[i][products[j].uuid].price_points = products[j].price_points;

                    // Add to the product counts
                    productCounts[products[j].uuid] = ObjectPool.Get() as PromotionCalculationProductCount;
                    productCounts[products[j].uuid].criteria_quantity = products[j].quantity;
                    productCounts[products[j].uuid].total_quantity = 0;
                    productCounts[products[j].uuid].criteria_price = products[j][priceField];
                    productCounts[products[j].uuid].total_price = 0;

                    // Add to the product quantities if required
                    if(typeof productQuantities[products[j].uuid] === "undefined") {
                        groupedProducts[products[j].uuid] = ObjectPool.Get() as PromotionCalculationProductGroup;
                        groupedProducts[products[j].uuid].quantity = products[j].quantity;
                        groupedProducts[products[j].uuid].base = products[j].everyday;
                        groupedProducts[products[j].uuid].finished = false;

                        productQuantities[products[j].uuid] = ObjectPool.Get() as { finished: boolean; };
                        productQuantities[products[j].uuid].finished = false;

                        hasUnfinished = true;

                        if(!this.settings.cross_promotion_count) {
                            productQuantity += products[j].quantity;
                            basePrice += products[j].everyday;
                        }
                    }

                    if(!affectedProducts.includes(products[j].uuid)) {
                        affectedProducts.push(products[j].uuid);
                    }
                } else {
                    if(this.promotion.criteria[i].exclude_count_quantity) {
                        promoProducts[i][products[j].uuid] = ObjectPool.Get() as PromotionCalculationProduct;
                        promoProducts[i][products[j].uuid].quantity = quantity;
                        promoProducts[i][products[j].uuid].price = products[j][priceField];
                        promoProducts[i][products[j].uuid].base = products[j].everyday;
                        promoProducts[i][products[j].uuid].adjustable = false;
                        promoProducts[i][products[j].uuid].uuid = products[j].uuid;
                        promoProducts[i][products[j].uuid].price_points = products[j].price_points;

                        // Add to the product counts
                        productCounts[products[j].uuid] = ObjectPool.Get() as PromotionCalculationProductCount;
                        productCounts[products[j].uuid].criteria_quantity = products[j].quantity;
                        productCounts[products[j].uuid].total_quantity = 0;
                        productCounts[products[j].uuid].criteria_price = products[j][priceField];
                        productCounts[products[j].uuid].total_price = 0;

                        // Add to the product quantities if required
                        if(typeof productQuantities[products[j].uuid] === "undefined") {
                            groupedProducts[products[j].uuid] = ObjectPool.Get() as PromotionCalculationProductGroup;
                            groupedProducts[products[j].uuid].quantity = products[j].quantity;
                            groupedProducts[products[j].uuid].base = products[j].everyday;
                            groupedProducts[products[j].uuid].finished = false;

                            productQuantities[products[j].uuid] = ObjectPool.Get() as { finished: boolean; };
                            productQuantities[products[j].uuid].finished = false;

                            hasUnfinished = true;

                            if(!this.settings.cross_promotion_count) {
                                productQuantity += products[j].quantity;
                                basePrice += products[j].everyday;
                            }
                        }
                    }
                }
            }

            // Loop through the global products if we're using cross promotion count
            if(this.settings.cross_promotion_count) {
                for(let j = 0, n = this.globalProducts.length; j < n; j++) {
                    if(isSpecialProduct(this.globalProducts[j])) continue;

                    if(this.globalProducts[j].quantity <= 0) continue;

                    if(typeof productCounts[this.globalProducts[j].uuid] !== "undefined") {
                        // Add to the total
                        productCounts[this.globalProducts[j].uuid].total_quantity += this.globalProducts[j].quantity;
                        productCounts[this.globalProducts[j].uuid].total_price +=
                            this.globalProducts[j].prices[priceField] as number;

                        // Add to the product quantities if required
                        // noinspection PointlessBooleanExpressionJS
                        if(productQuantities[this.globalProducts[j].uuid].finished === false) {
                            productQuantity += this.globalProducts[j].quantity;
                            basePrice += this.globalProducts[j].prices.normal;
                        }
                    } else {
                        // Check if the product is allowed to be added
                        const status = this.checkIfProductInCriteria(
                            this.globalProducts[j],
                            this.promotion.criteria[i],
                            i
                        );

                        // Apply the status
                        if(status === "ignore") continue;

                        if(status === "include" || this.promotion.criteria[i].exclude_count_quantity) {
                            productCounts[this.globalProducts[j].uuid] = ObjectPool.Get() as
                                PromotionCalculationProductCount;
                            productCounts[this.globalProducts[j].uuid].criteria_quantity = 0;
                            productCounts[this.globalProducts[j].uuid].total_quantity = this.globalProducts[j].quantity;
                            productCounts[this.globalProducts[j].uuid].criteria_price = 0;
                            productCounts[this.globalProducts[j].uuid].total_price =
                                this.globalProducts[j].prices[priceField] as number;
                            productCounts[this.globalProducts[j].uuid].price_points =
                                this.globalProducts[j].price_points!;

                            // Add to the product quantities if required
                            if(typeof productQuantities[this.globalProducts[j].uuid] === "undefined") {
                                productQuantities[this.globalProducts[j].uuid] = ObjectPool.Get() as {
                                    finished: boolean;
                                };

                                productQuantities[this.globalProducts[j].uuid].finished = false;

                                hasUnfinished = true;

                                productQuantity += this.globalProducts[j].quantity;
                                basePrice += this.globalProducts[j].prices.normal;
                            }
                        }
                    }
                }

                // Loop through the product counts and add the additional quantity
                for(const j in productCounts) {
                    if(!Object.prototype.hasOwnProperty.call(productCounts, j)) continue;

                    const quantity = productCounts[j].total_quantity - productCounts[j].criteria_quantity;
                    const price = productCounts[j].total_price - productCounts[j].criteria_price;

                    if(quantity > 0 || price > 0) {
                        if(typeof promoProducts[i][j] === "undefined") {
                            promoProducts[i][j] = ObjectPool.Get() as PromotionCalculationProduct;
                            promoProducts[i][j].quantity = 0;
                            promoProducts[i][j].price = 0;
                            promoProducts[i][j].base = 0;
                            promoProducts[i][j].adjustable = false;
                            promoProducts[i][j].price_points = productCounts[j].price_points;
                        }

                        promoProducts[i][j].quantity += quantity;
                        promoProducts[i][j].price += price;
                    }
                }
            }

            // Release the product counts
            ObjectPool.Release(productCounts);
        }

        this.groupedProducts = groupedProducts;

        // Release the product quantities
        ObjectPool.Release(productQuantities);

        // Return the promotional products
        return {
            promoProducts,
            productQuantity,
            basePrice,
            affectedProducts,
        };
    }

    /**
     * Check whether the promotion provided is valid
     * @param promoProducts
     * @param availableQuantity
     * @param completePercent
     * @returns {{valid: boolean, max_multiplication: number}}
     * @private
     */
    protected checkPromotionIsValid(
        promoProducts: Array<Record<string, PromotionCalculationProduct>>,
        availableQuantity: number,
        completePercent: number
    ): {
        valid: boolean;
        maxMultiplication: number;
        minQuantity: number;
    } {
        // Check the available quantity first
        if(availableQuantity === 0) {
            const ret = ObjectPool.Get() as {
                valid: boolean;
                maxMultiplication: number;
                minQuantity: number;
            };

            ret.valid = false;
            ret.maxMultiplication = 0;
            ret.minQuantity = 0;

            return ret;
        }

        let valid = true;
        let criteriaMet = 0;
        let requiredCriteria = 0;
        let requiredMet = 0;
        let requiredQuantity = 0;
        let minimumQuantity = 0;
        let optionalCriteria = 0;
        const validCriteria = ArrayPool.Get<boolean>();

        const criteriaPercentage = ObjectPool.Get() as Record<number, number>;

        let maxMultiplication = Infinity;
        let spendAndGet = false;
        let minMaxPercentage = Infinity;

        // Loop through the criteria and check if they are valid
        for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
            // Check if the promotion is optional
            if(!this.promotion.criteria[i].optional) {
                requiredQuantity += this.promotion.criteria[i].buy_amount;
                requiredCriteria++;
                this.optionalOnly = false;
            } else {
                optionalCriteria++;
                this.requiredOnly = false;
            }

            // Check which field we should be comparing to
            const compare = this.promotion.criteria[i].buy_amount_type === "quantity" ? "quantity" : "base";

            if(compare === "base") spendAndGet = true;

            // Check the minimum quantity
            if(compare === "quantity" && !this.promotion.criteria[i].optional) {
                minimumQuantity += this.promotion.criteria[i].buy_amount;
            }

            // Check if we have met the buy amount
            let buyAmount = 0;
            let mixQuantity = 0;

            for(const j in promoProducts[i]) {
                if(!Object.prototype.hasOwnProperty.call(promoProducts[i], j)) continue;

                buyAmount += promoProducts[i][j][compare];
                mixQuantity += 1;

                promoProducts[i][j].usable_quantity = promoProducts[i][j][compare];

                if(this.promotion.criteria[i].mixed_only && this.promotion.criteria[i].exclude_price_points) {
                    if(
                        compare === "quantity" &&
                        typeof promoProducts[i][j].price_points !== "undefined" &&
                        promoProducts[i][j].price_points.length > 1 &&
                        promoProducts[i][j][compare] >= promoProducts[i][j].price_points[1].quantity
                    ) {
                        // Get sets and remove them from the promo
                        let remaining = promoProducts[i][j][compare];

                        for(let k = promoProducts[i][j].price_points.length - 1; k > 0; k--) {
                            while(remaining >= promoProducts[i][j].price_points[k].quantity) {
                                remaining -= promoProducts[i][j].price_points[k].quantity;
                            }

                            if(remaining === 0) break;
                        }

                        buyAmount -= promoProducts[i][j][compare];
                        buyAmount += remaining;

                        promoProducts[i][j].usable_quantity = remaining;
                    }
                }
            }

            // Add to percentages
            const percentageQuantity = this.promotion.criteria[i].buy_amount;

            this.promotion.criteria[i].additional_quantity = 0;

            if(this.promotion.criteria[i].min_quantity) {
                // percentageQuantity = this.promotion.criteria[i].min_quantity;
                this.promotion.criteria[i].additional_quantity =
                    this.promotion.criteria[i].buy_amount - (this.promotion.criteria[i].min_quantity as number);
            }

            criteriaPercentage[i] = buyAmount / percentageQuantity;
            this.promotion.criteria[i].mix_percentage = criteriaPercentage[i];

            // Normalise if required
            if(this.promotion.criteria[i].normal_multiplier) {
                (this.promotion.criteria[i].mix_percentage as number) /=
                    this.promotion.criteria[i].normal_multiplier as number;
            }

            if(criteriaPercentage[i] !== 0 && criteriaPercentage[i] < minMaxPercentage) {
                minMaxPercentage = criteriaPercentage[i];
            }

            let isValid = false;

            if(this.promotion.criteria[i].mixed_only && mixQuantity <= 1) {
                // NOTE:
                // Mixed only now is allowed for any products that are mixed, even if they equal the same quantity.
                // This is due to the exclude price points setting being used more.
                // Mark the criteria as invalid
                this.promotion.criteria[i].invalid = true;
            } else if(buyAmount >= this.promotion.criteria[i].buy_amount) {
                // Criteria is valid
                criteriaMet++;
                isValid = true;

                if(!this.promotion.criteria[i].optional) {
                    requiredMet++;
                }
            } else if(this.promotion.mix_criteria && this.promotion.criteria[i].optional) {
                // Do nothing
                isValid = true;
            } else if(this.promotion.criteria[i].mix_percentage === 0) {
                // Mark the criteria as invalid
                this.promotion.criteria[i].invalid = true;
            } else {
                // Mark the criteria as invalid
                this.promotion.criteria[i].invalid = true;
            }

            validCriteria.push(isValid);
        }

        // Check if we are using criteria percentages
        let ignoreRequiredMet = false;

        if(this.promotion.mix_criteria) {
            let totalPercentage = 0;

            for(const i in criteriaPercentage) {
                if(!Object.prototype.hasOwnProperty.call(criteriaPercentage, i)) continue;

                totalPercentage += criteriaPercentage[i];
            }

            if(totalPercentage < completePercent) {
                valid = false;
            } else {
                criteriaMet++;
                ignoreRequiredMet = true;
                maxMultiplication = Math.floor(totalPercentage);
            }

            // Normalise mix percentage based on min amount if base criteria
            if(!this.promotion.calculate_only && !this.settings.cross_promotion_count) {
                for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
                    if(this.promotion.criteria[i].invalid) continue;

                    // Skip if mix percentage is minimum
                    if(this.promotion.criteria[i].mix_percentage !== minMaxPercentage) {
                        // Get normalising multiplier
                        const normalMultiplier =
                            (this.promotion.criteria[i].mix_percentage as number) / minMaxPercentage;

                        // Normalise the mix percentage
                        this.promotion.criteria[i].mix_percentage =
                            (this.promotion.criteria[i].mix_percentage as number) / normalMultiplier;

                        this.promotion.criteria[i].normal_multiplier = normalMultiplier;

                        // Duplicate the normalised criteria
                        for(let j = 1; j < Math.trunc(normalMultiplier); j++) {
                            // NOTE: You can push onto the end of the looped array
                            // cause the length isn't dynamically checked
                            this.promotion.criteria.push(Cloner.Clone(this.promotion.criteria[i]));
                        }
                    }
                }
            }
        }

        if(criteriaMet === 0) {
            valid = false;
        } else if(!this.optionalOnly && (ignoreRequiredMet ? criteriaMet : requiredMet) < requiredCriteria) {
            valid = false;
        } else if(requiredQuantity > availableQuantity && !spendAndGet) {
            valid = false;
        }

        if(valid && !this.promotion.mix_criteria) {
            for(const i in criteriaPercentage) {
                if(!Object.prototype.hasOwnProperty.call(criteriaPercentage, i)) continue;

                if(this.promotion.criteria[i].invalid) continue;

                if(
                    this.promotion.criteria[i].quantity_type === "more" &&
                    !this.promotion.criteria[i].min_quantity ||
                    this.promotion.criteria[i].optional
                ) continue; // No point checking this

                if(maxMultiplication > criteriaPercentage[i]) {
                    // maxMultiplication = Math.floor(criteriaPercentage[i]);
                    maxMultiplication = criteriaPercentage[i];
                }
            }
        }

        this.criteriaMet = criteriaMet;
        this.manager.cache.setCriteriaValid(this.promotion.uuid, validCriteria);

        // Clean up
        ObjectPool.Release(criteriaPercentage);

        const ret = ObjectPool.Get() as {
            valid: boolean;
            maxMultiplication: number;
            minQuantity: number;
        };

        ret.valid = valid;
        ret.maxMultiplication = maxMultiplication;
        ret.minQuantity = (valid && optionalCriteria === 0 && requiredCriteria === 1) ? minimumQuantity : 0;

        return ret;
    }

    /**
     * Get the quantity to adjust the current product by
     * @param product
     * @param usedQuantity
     * @param maxQuantity
     * @returns {{quantity: number, adjusted_quantity: number}}
     * @private
     */
    protected getQuantityToAdjust(
        product: PromotionCalculationProduct,
        usedQuantity: number,
        maxQuantity: number
    ): {
        quantity: number;
        adjustedQuantity: number;
    } {
        let quantity = product.quantity;
        let adjustedQuantity = 0;

        if(quantity + usedQuantity > maxQuantity) {
            quantity = maxQuantity - usedQuantity;
            adjustedQuantity += product.quantity - quantity;
        }

        return {
            quantity,
            adjustedQuantity,
        };
    }

    /**
     * Adjust the price and quantity to meet the used and max quantity
     */
    protected getAdjustedPriceAndQuantity(
        product: PromotionCalculationProduct,
        usedQuantity: number,
        maxQuantity: number,
        useBase = false
    ): {
        quantity: number;
        price: number;
        adjustedQuantity: number;
    } {
        const {
            quantity,
            adjustedQuantity,
        } = this.getQuantityToAdjust(product, usedQuantity, maxQuantity);

        let price;

        if(typeof product.uuid !== "undefined" && !useBase) {
            price = product.price - this.manager.calculateEverydayPrice(product.uuid, adjustedQuantity);
        } else {
            price = product.base / product.quantity * adjustedQuantity;
        }

        return {
            quantity,
            price,
            adjustedQuantity,
        };
    }

    /**
     * Get the criteria calculation quantities
     * @param criteria
     * @param products
     * @param mixed
     * @param maxMultiplication
     * @returns {{max_quantity: number, min_quantity: number, total_quantity: number, total_price: number}}
     * @private
     */
    protected getCriteriaCalculationQuantities(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        mixed: boolean,
        maxMultiplication: number
    ): {
        maxQuantity: number;
        minQuantity: number;
        totalQuantity: number;
        totalPrice: number;
    } {
        let maxQuantity = Infinity;
        const minQuantity = criteria.buy_amount;
        let totalPrice = 0;
        let totalQuantity = 0;

        if(criteria.quantity_type === "exact") {
            maxQuantity = criteria.buy_amount;
        } else if(criteria.buy_amount_max) {
            maxQuantity = criteria.buy_amount_max;
        }

        let multiplier = 1;

        // Loop through the products to calculate some variables
        for(const i in products) {
            if(!Object.prototype.hasOwnProperty.call(products, i)) continue;

            totalQuantity += products[i].quantity;
            totalPrice += products[i].price;
        }

        // Check the multiplier if required
        if(maxQuantity !== Infinity && !mixed) {
            multiplier = Math.floor(totalQuantity / minQuantity);
        } else if(maxQuantity !== Infinity && mixed) {
            if(Math.floor(criteria.mix_percentage) === criteria.mix_percentage) {
                // We check if it's a whole number so we don't have to recalculate,
                // this works well for criteria that are exact multiples (e.g. 1 and 10
                // and the promotion is for 1 / 10)
                multiplier = criteria.mix_percentage;
            } else {
                // If it isn't a whole number it is a proper mix (e.g. 1 and 10 and the
                // promotion is 2 / 20)
                multiplier = Math.floor(totalQuantity / minQuantity / criteria.mix_percentage);
            }
        }

        // Multiply the max quantity by the multiplier
        maxQuantity = maxQuantity * multiplier;

        // Ensure the max quantity does not breach the max multiplication
        if(
            (
                !this.optionalOnly ||
                this.optionalOnly &&
                this.promotion.mix_criteria
            ) &&
            maxQuantity > criteria.buy_amount * maxMultiplication
        ) {
            maxQuantity = Math.floor(criteria.buy_amount * maxMultiplication);
        }

        // Adjust the max quantity by the continuous sell quantity
        if(criteria.min_quantity) {
            // Remove the additional quantity
            maxQuantity -= (maxQuantity - criteria.buy_amount) % criteria.min_quantity;
        }

        if(criteria.additional_quantity) {
            // maxQuantity += criteria.additional_quantity;
        }

        // Return the min and max criteria
        return {
            maxQuantity,
            minQuantity,
            totalQuantity,
            totalPrice,
        };
    }

    /**
     * Adjust the provided product's price by the rate
     */
    protected adjustCriteriaProductByRate(
        product: PromotionCalculationProduct,
        rate: number,
        used: number,
        max: number,
        ignorePricePointCheck = false
    ): number {
        let quantity = product.quantity;
        let adjustedQuantity = 0;

        // Check if we can't use that quantity
        if(quantity > product.usable_quantity) {
            adjustedQuantity += quantity - product.usable_quantity;
            quantity = product.usable_quantity;
        }

        // Check if the rate is worse than an everyday price
        if(!ignorePricePointCheck) {
            for(let i = product.price_points.length - 1; i >= 0; i--) {
                if(product.price_points[i].quantity <= quantity) {
                    if(product.price_points[i].price / product.price_points[i].quantity < rate) {
                        const quantityToAdjust = product.price_points[i].quantity * Math.floor(
                            quantity / product.price_points[i].quantity
                        );

                        if(quantityToAdjust === 0) {
                            break;
                        }

                        adjustedQuantity += quantityToAdjust;
                        quantity -= quantityToAdjust;
                    } else {
                        break;
                    }
                }
            }
        }

        // Check if we need to adjust the quantity
        if(quantity + used > max) {
            quantity = max - used;
            adjustedQuantity += product.quantity - quantity;
        }

        if(quantity === 0) {
            return 0;
        }

        // Adjust the price
        let price = quantity * rate;

        // Determine the additional price
        if(adjustedQuantity !== 0) {
            if(typeof product.uuid !== "undefined") {
                // For normal products
                price += this.manager.calculateEverydayPrice(product.uuid, adjustedQuantity);
            } else {
                // For additional quantity products
                price += product.base / product.quantity * adjustedQuantity;
            }
        }

        // Set the promotional price
        product.price = price;

        if(quantity !== 0) {
            if(typeof product.comparison === "undefined") {
                product.comparison = ObjectPool.Get() as {
                    quantity: number;
                    price: number;
                };
                product.comparison.quantity = 0;
                product.comparison.price = 0;
            }

            product.comparison.quantity += quantity;
            product.comparison.price += quantity * rate;
        }

        return quantity;
    }

    /**
     * Adjust the usable quantities for the provided product
     */
    protected adjustUsableQuantity(
        products: Record<string, PromotionCalculationProduct>,
        uuid: string,
        quantities: Record<string, number>
    ): void {
        if(this.optionalOnly) return;

        if(uuid === "additional") return;

        if(typeof quantities[uuid] === "undefined") return;

        if(typeof this.groupedProducts[uuid] === "undefined") return;

        const used = quantities[uuid];
        const available = this.groupedProducts[uuid].quantity;
        let usable = products[uuid].usable_quantity;

        if(used >= available) {
            usable = 0;
        } else if(used + usable > available) {
            usable = available - used;
        }

        products[uuid].usable_quantity = usable;
    }

    /**
     * Update the used quantities for the product
     */
    protected updateUsedQuantities(uuid: string, used: number, quantities: Record<string, number>): void {
        if(typeof quantities[uuid] === "undefined") {
            quantities[uuid] = 0;
        }

        quantities[uuid] += used;
    }

    /**
     * Adjust the max multiplications that can be used (when mixing criteria)
     */
    protected adjustMaxMultiplication(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        maxMultiplication: number,
        usedQuantity: number,
        maxQuantity: number
    ): number {
        if(
            !this.settings.cross_promotion_count &&
            maxQuantity !== Infinity &&
            usedQuantity < criteria.buy_amount &&
            criteria.buy_amount_type === "quantity" &&
            !criteria.optional &&
            !this.promotion.mix_criteria
        ) {
            return 0;
        }

        if(!this.promotion.mix_criteria || maxQuantity === Infinity) return maxMultiplication;

        // Determine how much of the max quantity we used
        maxMultiplication -= usedQuantity / maxQuantity;

        return maxMultiplication;
    }

    /**
     * Calculate the sell amount using the provided rate and max quantity
     */
    protected calculateCriteriaSellAmount(
        products: Record<string, PromotionCalculationProduct>,
        rate: number,
        maxQuantity: number,
        minQuantity: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        let usedQuantity = 0;

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const used = this.adjustCriteriaProductByRate(
                products[i],
                rate,
                usedQuantity,
                maxQuantity,
                insideBranch || (maxQuantity % minQuantity === 0 && products[i].quantity % minQuantity === 0)
            );

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return usedQuantity;
    }

    /**
     * Calculate the sell total value for the criteria
     */
    protected calculateCriteriaSellTotal(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
            minQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        const rate = criteria.discount_amount / minQuantity;

        const usedQuantity = this.calculateCriteriaSellAmount(
            products,
            rate,
            maxQuantity,
            minQuantity,
            usedQuantities,
            insideBranch,
            productOrder
        );

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate the sell item value for the criteria
     */
    protected calculateCriteriaSellItem(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
            minQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        const rate = criteria.discount_amount;

        const usedQuantity = this.calculateCriteriaSellAmount(
            products,
            rate,
            maxQuantity,
            minQuantity,
            usedQuantities,
            insideBranch,
            productOrder
        );

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate the discount item value for the criteria
     * @param criteria
     * @param products
     * @param maxMultiplication
     * @param usedQuantities
     * @param insideBranch
     * @param productOrder
     * @private
     */
    protected calculateCriteriaDiscountItemAmount(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        let usedQuantity = 0;

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const {
                price,
                quantity,
            } = this.getAdjustedPriceAndQuantity(products[i], usedQuantity, maxQuantity);

            const rate = (price / quantity) - criteria.discount_amount;

            const used = this.adjustCriteriaProductByRate(
                products[i],
                rate,
                usedQuantity,
                maxQuantity,
                insideBranch
            );

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate the discount total value for the criteria
     * @param criteria
     * @param products
     * @param maxMultiplication
     * @param usedQuantities
     * @param insideBranch
     * @param productOrder
     * @private
     */
    protected calculateCriteriaDiscountTotalAmount(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        const {
            minQuantity,
            maxQuantity,
            totalQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        let usedQuantity = 0;
        const discountRate = criteria.discount_amount /
            (criteria.buy_amount_type === "amount" ? totalQuantity : minQuantity);

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const {
                price,
                quantity,
            } = this.getAdjustedPriceAndQuantity(products[i], usedQuantity, maxQuantity);

            const rate = ((criteria.buy_amount_type === "amount" ? products[i].base : price) / quantity) - discountRate;

            const used = this.adjustCriteriaProductByRate(
                products[i],
                rate,
                usedQuantity,
                maxQuantity,
                insideBranch
            );

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate the discount percentage value for the criteria
     * @param criteria
     * @param products
     * @param maxMultiplication
     * @param usedQuantities
     * @param insideBranch
     * @param productOrder
     * @private
     */
    protected calculateCriteriaDiscountPercentage(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        insideBranch: boolean,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        let usedQuantity = 0;

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const {
                price,
                quantity,
            } = this.getAdjustedPriceAndQuantity(products[i], usedQuantity, maxQuantity);

            const individual = price / quantity;
            const rate = individual - (individual * (criteria.discount_amount / 100));

            const used = this.adjustCriteriaProductByRate(
                products[i],
                rate,
                usedQuantity,
                maxQuantity,
                insideBranch
            );

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate the criteria sell rate
     * @param criteria
     * @param products
     * @param maxMultiplication
     * @param usedQuantities
     * @param productOrder
     * @private
     */
    protected calculateCriteriaSellRate(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        let usedQuantity = 0;

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const rate = this.manager.calculateEverydayPrice(
                i,
                criteria.discount_amount
            ) / criteria.discount_amount;

            const used = this.adjustCriteriaProductByRate(products[i], rate, usedQuantity, maxQuantity, true);

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Calculate a quantity only criteria
     * @param criteria
     * @param products
     * @param maxMultiplication
     * @param usedQuantities
     * @param productOrder
     * @returns {Promise<void>}
     * @private
     */
    protected calculateQuantityOnly(
        criteria: Required<PromotionCalculationPromotionCriteria>,
        products: Record<string, PromotionCalculationProduct>,
        maxMultiplication: number,
        usedQuantities: Record<string, number>,
        productOrder: Array<string>
    ): number {
        const {
            maxQuantity,
        } = this.getCriteriaCalculationQuantities(criteria, products, this.promotion.mix_criteria, maxMultiplication);

        let usedQuantity = 0;

        for(const i of productOrder) {
            if(typeof products[i] === "undefined") {
                continue;
            }

            this.adjustUsableQuantity(products, i, usedQuantities);

            const used = this.adjustCriteriaProductByRate(
                products[i],
                products[i].price / products[i].quantity,
                usedQuantity,
                maxQuantity,
                true
            );

            this.updateUsedQuantities(i, used, usedQuantities);
            usedQuantity += used;

            if(usedQuantity >= maxQuantity) {
                break;
            }
        }

        return this.adjustMaxMultiplication(criteria, maxMultiplication, usedQuantity, maxQuantity);
    }

    /**
     * Run the promotion calculation on a promotion
     */
    protected runPromotion(
        products: Array<Record<string, PromotionCalculationProduct>>,
        maxMultiplication: number,
        insideBranch: boolean, // This is required to ensure the promotion calculation finishes in a reasonable time
        productOrder: Array<string>
    ): Record<string, number> {
        // Loop through the criteria
        const usedQuantities = ObjectPool.Get() as Record<string, number>;

        let currentMaxMultiplication = maxMultiplication;

        for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
            const criteria = this.promotion.criteria[i] as Required<PromotionCalculationPromotionCriteria>;

            if(criteria.invalid) continue;

            if(criteria.normal_multiplier) {
                criteria.mix_percentage *= criteria.normal_multiplier;
                criteria.buy_amount /= criteria.normal_multiplier;
                criteria.discount_amount /= criteria.normal_multiplier;
            }

            let criteriaMaxMultiplication =
                criteria.quantity_type === "more" && criteria.min_quantity === null ?
                    Infinity :
                    currentMaxMultiplication;

            // Calculate the criteria
            if(criteria.type === "sell-total") {
                criteriaMaxMultiplication = this.calculateCriteriaSellTotal(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    insideBranch,
                    productOrder
                );
            } else if(criteria.type === "sell-item") {
                criteriaMaxMultiplication = this.calculateCriteriaSellItem(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    insideBranch,
                    productOrder
                );
            } else if(criteria.type === "discount-item-amount") {
                criteriaMaxMultiplication = this.calculateCriteriaDiscountItemAmount(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    insideBranch,
                    productOrder
                );
            } else if(criteria.type === "discount-total-amount") {
                criteriaMaxMultiplication = this.calculateCriteriaDiscountTotalAmount(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    insideBranch,
                    productOrder
                );
            } else if(criteria.type === "discount-percentage") {
                criteriaMaxMultiplication = this.calculateCriteriaDiscountPercentage(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    insideBranch,
                    productOrder
                );
            } else if(criteria.type === "sell-rate") {
                criteriaMaxMultiplication = this.calculateCriteriaSellRate(
                    criteria,
                    products[i],
                    criteriaMaxMultiplication,
                    usedQuantities,
                    productOrder
                    // Note, inside branch is not required here as we skip the slow part
                );
            } else if(criteria.type === "quantity-only") {
                if(criteria.buy_amount_type === "quantity") {
                    criteriaMaxMultiplication = this.calculateQuantityOnly(
                        criteria,
                        products[i],
                        criteriaMaxMultiplication,
                        usedQuantities,
                        productOrder
                        // Note, inside branch is not required here as we skip the slow part
                    );
                } // Don't do anything for spend promotions
            } else {
                throw new CalculationException("Invalid Criteria Type");
            }

            if(criteria.normal_multiplier) {
                criteria.mix_percentage /= criteria.normal_multiplier;
                criteria.buy_amount *= criteria.normal_multiplier;
                criteria.discount_amount *= criteria.normal_multiplier;
            }

            if(isFinite(criteriaMaxMultiplication)) {
                currentMaxMultiplication = criteriaMaxMultiplication;

                if(currentMaxMultiplication === 0 && !this.promotion.mix_criteria) {
                    // Mix criteria supports the max multiplication being 0
                    this.promotionIsInvalid = true;

                    break;
                }
            }
        }

        // Release the used quantities
        return usedQuantities;
    }

    /**
     * Assign the products to their respective criteria to update them
     * @param products
     * @param maxQuantity
     * @param basePrice
     * @returns {*}
     * @private
     */
    protected assignCriteriaToProducts(
        products: Array<Record<string, Required<PromotionCalculationProduct>>>,
        maxQuantity: number,
        basePrice: number
    ): Array<Record<string, PromotionCalculationProduct>> {
        // If we have only one criteria always return the products
        if(this.promotion.criteria.length === 1) return products;

        if(this.criteriaMet <= 1) return products;

        if(this.requiredOnly) {
            // Do nothing at the moment
        } else if(this.promotion.mix_criteria) {
            // Do nothing at the moment
        // eslint-disable-next-line no-constant-condition
        } else if(!this.optionalOnly && false) {
            // Do nothing at the moment
            // OLD:
            // We are mixing between required and optional
            // Check if we should use the optional criteria or not
        } else if(this.optionalOnly && !this.promotion.mix_criteria) {
            // Regroup the products
            const regrouped = ObjectPool.Get() as Record<string, {
                criteria: Array<{
                    quantity: number;
                    price: number;
                    ratio: number;
                }>;
                pricePoints: Array<SalePromotionProductPrice>;
            }>;

            for(let i = 0, l = products.length; i < l; i++) {
                // Products is actually the criteria (poorly named)
                // Loop through the criteria products and add them to regrouped
                for(const j in products[i]) {
                    if(!Object.prototype.hasOwnProperty.call(products[i], j)) {
                        continue;
                    }

                    if(typeof products[i][j] !== "object") { // For some reason, the length property is assigned a value
                        continue;
                    }

                    if(!(j in regrouped)) {
                        regrouped[j] = ObjectPool.Get() as {
                            criteria: Array<{
                                quantity: number;
                                price: number;
                                ratio: number;
                            }>;
                            pricePoints: Array<SalePromotionProductPrice>;
                        };

                        regrouped[j].criteria = ArrayPool.Get();
                        regrouped[j].pricePoints = products[i][j].price_points;
                    }

                    const criteria = ObjectPool.Get() as {
                        quantity: number;
                        price: number;
                        ratio: number;
                    };

                    if(products[i][j].comparison) {
                        criteria.quantity = products[i][j].comparison.quantity;
                        criteria.price = products[i][j].comparison.price;
                    } else {
                        criteria.quantity = products[i][j].quantity;
                        criteria.price = products[i][j].price;
                    }

                    criteria.ratio = criteria.price / criteria.quantity;

                    regrouped[j].criteria.push(criteria);
                }
            }

            // Loop through the regrouped products
            const prods = ObjectPool.Get() as Record<string, PromotionCalculationProduct>;

            for(const i in regrouped) {
                if(!Object.prototype.hasOwnProperty.call(regrouped, i)) {
                    continue;
                }

                // Misc variables
                let maxQuantity = 0;

                // Create the knapsack object
                const possibilities = Knapsack.getObject<number | string>();

                // Add the everyday prices to the knapsack
                for(let j = 0, n = regrouped[i].pricePoints.length; j < n; j++) {
                    possibilities.prices.push(regrouped[i].pricePoints[j].price);
                    possibilities.quantities.push(regrouped[i].pricePoints[j].quantity);
                    possibilities.identifiers.push(`edp-${j}`);
                    possibilities.maxQuantities.push(Infinity);
                }

                // Add the criteria to the knapsack
                for(let j = 0, n = regrouped[i].criteria.length; j < n; j++) {
                    possibilities.prices.push(regrouped[i].criteria[j].ratio);
                    possibilities.quantities.push(1);
                    possibilities.identifiers.push(j);
                    possibilities.maxQuantities.push(regrouped[i].criteria[j].quantity);

                    maxQuantity = Math.max(maxQuantity, regrouped[i].criteria[j].quantity);
                }

                let productQuantity = maxQuantity;

                for(let j = 0, n = this.products.length; j < n; j++) {
                    if(this.products[j].uuid === i) {
                        productQuantity = this.products[j].quantity;
                        break;
                    }
                }

                // Solve the knapsack
                const ks = Knapsack.solve(possibilities, productQuantity, true);

                // Reapply the prices
                let price = 0;

                for(let j = 0, n = ks.used.length; j < n; j++) {
                    if(typeof ks.used[j] === "string") {
                        // Everyday price
                        const priceIndex = parseInt((ks.used[j] as string).split("edp-")[1]);

                        price += regrouped[i].pricePoints[priceIndex].price;

                        continue;
                    }

                    // Criteria
                    price += regrouped[i].criteria[ks.used[j] as number].ratio;
                }

                // Get the base price
                let base = 0;

                for(let j = 0, n = this.products.length; j < n; j++) {
                    if(this.products[j].uuid === i) {
                        base = this.products[j].base;
                        break;
                    }
                }

                prods[i] = {
                    price,
                    base,
                    quantity       : ks.usedQuantity,
                    uuid           : i,
                    adjustable     : true,
                    price_points   : [],
                    usable_quantity: 0,
                    comparison     : {
                        price,
                        quantity: ks.usedQuantity,
                    },
                };

                ObjectPool.Release(possibilities);
                ObjectPool.Release(ks);
            }

            // Reformat the products to match the original object
            const reformatted = ArrayPool.Get<Record<string, PromotionCalculationProduct>>();

            reformatted.push(prods);

            // Clean up old objects
            ObjectPool.Release(regrouped);
            ObjectPool.Release(products);

            return reformatted;
        } else {
            // We are mixing between required and optional
            // Split by criteria to attempt to find the best price
            // NOTE: If something doesn't work after converting to TypeScript, it's probably because of this block

            // Use knapsack to determine which criteria it should be assigned to
            const possibilities = Knapsack.getObject();

            // Create the knapsack map object
            const ksMap = ObjectPool.Get() as Record<string, number>;

            // Create a "single" version of the promotion
            const single = ObjectPool.Get() as {
                price: number;
                quantity: number;
            };

            single.price = 0;
            single.quantity = 0;

            // Create the prices from the criteria
            for(let i = 0, l = products.length; i < l; i++) {
                let price = 0;
                let quantity = 0;
                let morePrice = 0;
                let moreQuantity = 0;
                let maxQuantity = Infinity;

                for(const j in products[i]) {
                    if(!Object.prototype.hasOwnProperty.call(products[i], j)) continue;

                    if(typeof products[i][j].comparison === "undefined") continue;

                    price += products[i][j].comparison.price;
                    quantity += products[i][j].comparison.quantity;
                    morePrice += products[i][j].price;
                    moreQuantity += products[i][j].quantity;

                    single.price += products[i][j].comparison.price;
                    single.quantity += products[i][j].comparison.quantity;
                }

                if(quantity > this.promotion.criteria[i].buy_amount) {
                    const adjustment = quantity / this.promotion.criteria[i].buy_amount;

                    price = price / quantity * this.promotion.criteria[i].buy_amount;
                    quantity = this.promotion.criteria[i].buy_amount;

                    for(const j in products[i]) {
                        if(!Object.prototype.hasOwnProperty.call(products[i], j)) continue;

                        if(typeof products[i][j].comparison === "undefined") continue;

                        products[i][j].comparison.price = products[i][j].comparison.price / adjustment;
                        products[i][j].comparison.quantity = products[i][j].comparison.quantity / adjustment;
                    }
                }

                if(price === 0 && quantity === 0) {
                    continue;
                }

                if(!this.optionalOnly) {
                    maxQuantity = Math.floor(this.maxMultiplication);
                }

                possibilities.prices.push(price / quantity);
                possibilities.quantities.push(1);
                possibilities.identifiers.push(i.toString());
                possibilities.maxQuantities.push(maxQuantity === Infinity ? quantity : maxQuantity);
                ksMap[i.toString()] = possibilities.prices.length - 1;

                // Add the more than options
                if(this.promotion.criteria[i].quantity_type === "more") {
                    possibilities.prices.push(morePrice);
                    possibilities.quantities.push(moreQuantity);
                    possibilities.identifiers.push(i.toString());
                    possibilities.maxQuantities.push(Infinity);
                    ksMap[i.toString()] = possibilities.prices.length - 1;
                }
            }

            // Create the base price
            possibilities.prices.push(basePrice);
            possibilities.quantities.push(maxQuantity);
            possibilities.identifiers.push("base");
            possibilities.maxQuantities.push(Infinity);
            ksMap["base"] = possibilities.prices.length - 1;

            // Add a single as a possibility
            possibilities.prices.push(single.price);
            possibilities.quantities.push(single.quantity);
            possibilities.identifiers.push("single");
            possibilities.maxQuantities.push(this.maxMultiplication);
            ksMap["single"] = possibilities.prices.length - 1;

            // Calculate the solution
            const ks = Knapsack.solve(possibilities, maxQuantity, true);

            // Check if the solution contains the single promotion
            let containsSingle = false;

            for(let i = 0, l = ks.used.length; i < l; i++) {
                if(ks.used[i] === "single") {
                    containsSingle = true;
                    break;
                }
            }

            const prods = ArrayPool.Get<Record<string, PromotionCalculationProduct>>();

            const criteriaAdjustment = ObjectPool.Get() as Record<number, Record<string, PromotionCalculationProduct>>;

            for(let i = 0, l = ks.used.length; i < l; i++) {
                // Do nothing with the base
                if(ks.used[i] === "base") {
                    // Distribute the price across all products
                    for(let j = 0, n = products.length; j < n; j++) {
                        // Adjust to use the comparison rates
                        if(typeof criteriaAdjustment[j] === "undefined") {
                            criteriaAdjustment[j] = ObjectPool.Get() as Record<string, PromotionCalculationProduct>;
                        }

                        let broken = false;

                        for(const k in products[j]) {
                            if(!Object.prototype.hasOwnProperty.call(products[j], k)) continue;

                            if(typeof products[j][k].comparison === "undefined") continue;

                            if(typeof criteriaAdjustment[j][k] === "undefined") {
                                criteriaAdjustment[j][k] = ObjectPool.Get() as PromotionCalculationProduct;
                                criteriaAdjustment[j][k].adjustable = products[j][k].adjustable;
                                criteriaAdjustment[j][k].base = 0;
                                criteriaAdjustment[j][k].quantity = 0;
                                criteriaAdjustment[j][k].price = 0;
                                criteriaAdjustment[j][k].comparison = ObjectPool.Get() as {
                                    quantity: number;
                                    price: number;
                                }; // To ensure the product gets used
                            }

                            criteriaAdjustment[j][k].base += basePrice / maxQuantity;
                            criteriaAdjustment[j][k].quantity += 1;
                            criteriaAdjustment[j][k].price += basePrice / maxQuantity;
                            broken = true;
                            break;
                        }

                        if(broken) {
                            break;
                        }
                    }

                    continue;
                }

                // Do nothing with the single
                if(ks.used[i] === "single") {
                    for(let j = 0, n = products.length; j < n; j++) {
                        // Adjust to use the comparison rates
                        if(typeof criteriaAdjustment[j] === "undefined") {
                            criteriaAdjustment[j] = ObjectPool.Get() as Record<string, PromotionCalculationProduct>;
                        }

                        for(const k in products[j]) {
                            if(!Object.prototype.hasOwnProperty.call(products[j], k)) continue;

                            if(typeof products[j][k].comparison === "undefined") continue;

                            if(typeof criteriaAdjustment[j][k] === "undefined") {
                                criteriaAdjustment[j][k] = ObjectPool.Get() as PromotionCalculationProduct;
                                criteriaAdjustment[j][k].adjustable = products[j][k].adjustable;
                                criteriaAdjustment[j][k].base = 0;
                                criteriaAdjustment[j][k].quantity = 0;
                                criteriaAdjustment[j][k].price = 0;
                                criteriaAdjustment[j][k].comparison = ObjectPool.Get() as {
                                    quantity: number;
                                    price: number;
                                }; // To ensure the product gets used
                            }

                            criteriaAdjustment[j][k].base +=
                                products[j][k].base / products[j][k].quantity * products[j][k].comparison.quantity;
                            criteriaAdjustment[j][k].quantity += products[j][k].comparison.quantity;
                            criteriaAdjustment[j][k].price += products[j][k].comparison.price;
                        }
                    }

                    // We can't do anything else
                    break;
                }

                if(containsSingle) continue;

                // Adjust to use the comparison rates
                const adjustmentKey = ksMap[ks.used[i]]; // This was previously ks.used[i]

                if(typeof criteriaAdjustment[adjustmentKey] === "undefined") {
                    criteriaAdjustment[adjustmentKey] = ObjectPool.Get() as Record<string, PromotionCalculationProduct>;
                }

                for(const j in products[adjustmentKey]) {
                    if(!Object.prototype.hasOwnProperty.call(products[adjustmentKey], j)) continue;

                    if(typeof products[adjustmentKey][j].comparison === "undefined") continue;

                    if(typeof criteriaAdjustment[adjustmentKey][j] === "undefined") {
                        criteriaAdjustment[adjustmentKey][j] = ObjectPool.Get() as PromotionCalculationProduct;
                        criteriaAdjustment[adjustmentKey][j].adjustable = products[adjustmentKey][j].adjustable;
                        criteriaAdjustment[adjustmentKey][j].base = 0;
                        criteriaAdjustment[adjustmentKey][j].quantity = 0;
                        criteriaAdjustment[adjustmentKey][j].price = 0;
                        criteriaAdjustment[adjustmentKey][j].comparison = ObjectPool.Get() as {
                            quantity: number;
                            price: number;
                        }; // To ensure the product gets used
                    }

                    criteriaAdjustment[adjustmentKey][j].base +=
                        products[adjustmentKey][j].base /
                        products[adjustmentKey][j].quantity *
                        possibilities.quantities[ksMap[ks.used[i]]];
                    criteriaAdjustment[adjustmentKey][j].quantity += possibilities.quantities[ksMap[ks.used[i]]];
                    criteriaAdjustment[adjustmentKey][j].price += possibilities.prices[ksMap[ks.used[i]]];

                    /*
                    criteriaAdjustment[ks.used[i]][j].base +=
                        products[ks.used[i]][j].base /
                        products[ks.used[i]][j].quantity *
                        products[ks.used[i]][j].comparison.quantity;
                    criteriaAdjustment[ks.used[i]][j].quantity += products[ks.used[i]][j].comparison.quantity;
                    criteriaAdjustment[ks.used[i]][j].price    += products[ks.used[i]][j].comparison.price;
                    */
                }
            }

            // Override the products using the comparison rates
            for(const i in criteriaAdjustment) {
                if(!Object.prototype.hasOwnProperty.call(criteriaAdjustment, i)) continue;

                // products[i] = criteriaAdjustment[i];
                prods.push(criteriaAdjustment[i]);
            }

            products = prods as Array<Record<string, Required<PromotionCalculationProduct>>>;

            // Clean up
            ObjectPool.Release(possibilities);
            ObjectPool.Release(ks);
            // ObjectPool.Release(criteriaAdjustment); // <- Used by reference in prods
        }

        return products;
    }

    /**
     * Update the original products using the promotional products array
     */
    protected updateProductPricesFromPromotion(
        promoProduct: Array<Record<string, PromotionCalculationProduct>>,
        products: Array<PromotionManagerProduct>
    ): number {
        const groupedProducts = ObjectPool.Get() as Record<string, {
            quantity: number;
            price: number;
            compare_price: number;
            compare_quantity: number;
            compare_max: boolean;
            quantity_left: number;
        }>;

        let reduction = 0;

        for(let i = 0, l = promoProduct.length; i < l; i++) {
            for(const j in promoProduct[i]) {
                if(!Object.prototype.hasOwnProperty.call(promoProduct[i], j)) continue;

                // Make sure we only edit products we are allowed to
                if(promoProduct[i][j].adjustable === false) {
                    continue;
                }

                if(typeof promoProduct[i][j].comparison === "undefined") {
                    continue;
                }

                if(typeof groupedProducts[j] === "undefined") {
                    groupedProducts[j] = ObjectPool.Get() as {
                        quantity: number;
                        price: number;
                        compare_price: number;
                        compare_quantity: number;
                        compare_max: boolean;
                        quantity_left: number;
                    };

                    groupedProducts[j].quantity = 0;
                    groupedProducts[j].price = 0;
                    groupedProducts[j].compare_price = 0;
                    groupedProducts[j].compare_quantity = 0;
                    groupedProducts[j].compare_max = false;
                    groupedProducts[j].quantity_left = 0;
                }

                groupedProducts[j].quantity += promoProduct[i][j].quantity;
                groupedProducts[j].price += promoProduct[i][j].price;

                groupedProducts[j].compare_max = true;

                if(typeof promoProduct[i][j].comparison?.price === "undefined") {
                    groupedProducts[j].compare_quantity += promoProduct[i][j].quantity;
                    groupedProducts[j].compare_price += promoProduct[i][j].price;
                    groupedProducts[j].quantity_left += promoProduct[i][j].quantity;
                } else {
                    groupedProducts[j].compare_price += promoProduct[i][j].comparison?.price || 0;
                    groupedProducts[j].compare_quantity += promoProduct[i][j].comparison?.quantity || 0;
                    groupedProducts[j].quantity_left += promoProduct[i][j].comparison?.quantity || 0;
                }

                reduction += promoProduct[i][j].base - promoProduct[i][j].price;
            }
        }

        // Apply the rate to the products
        for(const i in groupedProducts) {
            if(!Object.prototype.hasOwnProperty.call(groupedProducts, i)) continue;

            const rate = groupedProducts[i].price / groupedProducts[i].quantity;

            for(let j = 0, n = products.length; j < n; j++) {
                if(products[j].uuid !== i) continue;

                products[j].promotion = rate * products[j].quantity;

                if(this.optionalOnly && this.promotion.criteria.length > 1) {
                    products[j].compare = {
                        price   : rate,
                        quantity: 1,
                        max     : false,
                    };
                } else {
                    const compareRate = groupedProducts[i].compare_price / groupedProducts[i].compare_quantity;
                    let quantity = products[j].quantity;

                    if(quantity > groupedProducts[i].quantity_left) {
                        quantity = Math.floor(groupedProducts[i].quantity_left);
                    }

                    if(quantity === 0 && groupedProducts[i].quantity !== groupedProducts[i].compare_quantity) {
                        products[j].compare = {
                            price   : groupedProducts[i].price - groupedProducts[i].compare_price,
                            quantity: groupedProducts[i].quantity - groupedProducts[i].compare_quantity,
                            max     : groupedProducts[i].compare_max,
                        };
                    } else {
                        const price = compareRate * quantity;

                        products[j].compare = {
                            price   : price,
                            quantity: quantity,
                            max     : groupedProducts[i].compare_max,
                        };

                        groupedProducts[i].quantity_left -= quantity;
                    }
                }
            }
        }

        // Clean up
        ObjectPool.Release(groupedProducts);

        return reduction;
    }

    /**
     * Check to see if there is any potential promotions for the leftover items
     */
    protected async checkAdditionalProducts(
        usedQuantities: Record<string, number>,
        productQuantities: Record<string, number>,
        originalPromotions: Array<PromotionCalculationPromotion>,
        originalId: string,
        branchDepth: number
    ): Promise<Array<Array<PromotionCalculationResultBranch>>> {
        if(this.shouldCancel()) {
            return [];
        }

        if(branchDepth > MAX_BRANCH_DEPTH) {
            if(getLogLevel() >= LogLevel.VERBOSE) {
                console.debug(`We exceeded the maximum branch depth allowed, exiting promotion ${originalId}`);
            }

            return [];
        }

        // Prepare leftover array
        const leftoverProducts = ArrayPool.Get<PromotionManagerProduct>();

        // Loop through products and calculate the leftovers
        for(let i = 0, l = this.products.length; i < l; i++) {
            // Calculate if there is another line with the same uuid
            let additionalQty = 0;

            if(
                typeof productQuantities[this.products[i].uuid] !== "undefined" &&
                productQuantities[this.products[i].uuid] !== 0
            ) {
                if(usedQuantities[this.products[i].uuid] !== this.products[i].quantity) {
                    additionalQty = productQuantities[this.products[i].uuid] - this.products[i].quantity;
                }
            }

            // Get the quantity used
            let used = 0;

            if(
                typeof usedQuantities[this.products[i].uuid] !== "undefined" &&
                usedQuantities[this.products[i].uuid] !== 0
            ) {
                used = usedQuantities[this.products[i].uuid] - additionalQty;
            }

            // Skip if nothing left
            if(this.products[i].quantity - used !== 0) {
                // Create new product item
                const leftoverProduct = Cloner.Clone(this.products[i]);

                const qty = leftoverProduct.quantity - used;

                // Subtract used and add it to the list
                if(qty !== leftoverProduct.quantity) {
                    leftoverProduct.everyday = this.manager.calculateEverydayPrice(leftoverProduct.uuid, qty);
                    leftoverProduct.quantity = qty;
                }

                leftoverProducts.push(leftoverProduct);
            }
        }

        // Setup branches
        const branches = ArrayPool.Get<Array<PromotionCalculationResultBranch>>();

        // Skip if empty
        if(leftoverProducts.length) {
            // Check for possible promotions from leftover products
            let possibilities = originalPromotions;

            if(!this.optionalOnly || originalPromotions.length === 0) {
                // Get next layer
                const available = await this.manager.determineAvailablePromotions(
                    leftoverProducts,
                    this.manager.currentOutlet,
                    this.manager.currentCustomer,
                    this.applicablePromotions
                );

                if(!possibilities) {
                    possibilities = ArrayPool.Get();
                }

                // Filter out any potential infinite recursion
                for(let i = 0, l = available.length; i < l; i++) {
                    if(available[i].uuid === originalId) {
                        continue;
                    }

                    possibilities.push(available[i]);
                }
            }

            // Loop though the child branches and calculate
            for(let i = 0, l = possibilities.length; i < l; i++) {
                if(this.shouldCancel()) {
                    break;
                }

                let res = AdvancedPromotionCache.getResult(possibilities[i], leftoverProducts);

                if(!res) {
                    const calc = new PromotionCalculation(
                        this.manager,
                        possibilities[i],
                        Cloner.Clone(leftoverProducts),
                        this.settings.cross_promotion_count,
                        this.shouldCancel,
                        this.applicablePromotions,
                        this.productCache,
                        this.globalProducts
                    );

                    res = await calc.run(originalId, branchDepth + 1);

                    AdvancedPromotionCache.storeResult(possibilities[i], leftoverProducts, res);
                }

                if(!res) {
                    continue;
                }

                branches.push(res as Array<PromotionCalculationResultBranch>);
            }
        }

        // Release the leftover products
        ArrayPool.Release(leftoverProducts);

        return branches;
    }

    /**
     * Prepare the results from the branches
     * @param promoProducts
     * @param productQuantities
     * @param reduction
     * @param productQuantity
     * @param usedQuantities
     * @param branches
     * @param currentPercent
     * @param maxPercent
     * @param minQuantity
     * @param affectedProducts
     * @private
     * @return Array
     */
    public prepareBranches(
        promoProducts: Array<Record<string, PromotionCalculationProduct>>,
        productQuantities: Record<string, number>,
        reduction: number,
        productQuantity: number,
        usedQuantities: Record<string, number>,
        branches: Array<Array<PromotionCalculationResultBranch>>,
        currentPercent: number,
        maxPercent: number,
        minQuantity: number,
        affectedProducts: Array<string>
    ): Array<PromotionCalculationResultBranch> {
        const emptyBranch = ArrayPool.Get<PromotionCalculationResultBranch>();
        const empty = ObjectPool.Get() as PromotionCalculationResultBranch;

        empty.uuids = ArrayPool.Get<string>();
        empty.promotions = ArrayPool.Get<PromotionCalculationPromotion>();
        empty.products = ArrayPool.Get();
        empty.reduction = 0;
        empty.quantity = 0;
        empty.total_price = 0;
        empty.used_quantities = ObjectPool.Get() as Record<string, number>;
        empty.minimumQuantity = ArrayPool.Get<number>();

        if(this.promotion.mix_criteria) {
            empty.total_percent = 0;
        }

        emptyBranch.push(empty);
        branches.push(emptyBranch);

        // Prepare the result
        const results = ArrayPool.Get<PromotionCalculationResultBranch>();

        // Loop through possibility branches
        for(const i in branches) {
            if(!Object.prototype.hasOwnProperty.call(branches, i)) continue;

            // Loop through promotion branches
            for(const j in branches[i]) {
                if(!Object.prototype.hasOwnProperty.call(branches[i], j)) continue;

                const result = ObjectPool.Get() as PromotionCalculationResultBranch;

                // Prepare the promotion array
                const promotion = ArrayPool.Get<PromotionCalculationPromotion>();

                promotion.push(this.promotion);

                const uuids = ArrayPool.Get<string>();

                uuids.push(this.promotion.uuid);

                const minimumQuantities = ArrayPool.Get<number>();

                minimumQuantities.push(minQuantity);

                // Prepare the products array
                const products = ArrayPool.Get<PromotionManagerProduct>();
                let totalPrice = 0;

                const quantities = ObjectPool.Get() as Record<string, number>;

                const checkFamilies = ArrayPool.Get<PromotionManagerProduct & {
                    index: number;
                    familyQuantity: number;
                }>();

                for(const p in this.products) {
                    if(!Object.prototype.hasOwnProperty.call(this.products, p)) continue;

                    const product = this.products[p];
                    let qty = product.quantity;

                    quantities[product.uuid] = 0;

                    // Calculate if there is another line with the same uuid
                    let additionalQty = 0;

                    if(productQuantities[product.uuid]) {
                        if(usedQuantities[product.uuid] !== product.quantity) {
                            additionalQty = productQuantities[product.uuid] - product.quantity;
                        }
                    }

                    // If it is used in promotion remove that from total
                    if(usedQuantities[product.uuid] - additionalQty) {
                        const newProduct = Cloner.Clone(product);

                        let promoProduct = null;

                        for(const promo in promoProducts) {
                            if(!Object.prototype.hasOwnProperty.call(promoProducts, promo)) continue;

                            if(promoProducts[promo][product.uuid]) {
                                if(!promoProducts[promo][product.uuid].comparison) continue;

                                promoProduct = promoProducts[promo][product.uuid];
                                break;
                            }
                        }

                        if(promoProduct) {
                            newProduct.promotion = promoProduct.comparison?.price || 0;
                            newProduct.quantity = promoProduct.comparison?.quantity || 0;
                            newProduct.in_promotion = true;

                            totalPrice += newProduct.promotion;

                            products.push(newProduct);

                            qty -= usedQuantities[product.uuid] - additionalQty;
                            quantities[product.uuid] += usedQuantities[product.uuid] - additionalQty;
                        }
                    }

                    // Remove quantity that has been used from branches
                    if(branches[i][j].used_quantities[product.uuid]) {
                        qty -= branches[i][j].used_quantities[product.uuid];
                        quantities[product.uuid] += branches[i][j].used_quantities[product.uuid];
                    }

                    // If there is still quantity leftover add it to total
                    if(qty) {
                        const newProduct = Cloner.Clone(product);

                        newProduct.everyday = this.manager.calculateEverydayPrice(newProduct.uuid, qty);
                        newProduct.promotion = newProduct.everyday;
                        newProduct.quantity = qty;
                        newProduct.in_promotion = affectedProducts.includes(newProduct.uuid);

                        totalPrice += newProduct.everyday;

                        quantities[product.uuid] += qty;

                        products.push(newProduct);

                        if(newProduct.family) {
                            checkFamilies.push({
                                ...newProduct,
                                index         : products.length - 1,
                                familyQuantity: qty,
                            });
                        }
                    }
                }

                if(checkFamilies.length > 1) {
                    const checkProducts = [];

                    for(let i = 0, l = checkFamilies.length; i < l; i++) {
                        let globalIndex = -1;

                        for(let j = 0, n = this.globalProducts.length; j < n; j++) {
                            if(this.globalProducts[j].uuid === checkFamilies[i].uuid) {
                                globalIndex = j;
                                break;
                            }
                        }

                        if(globalIndex === -1) {
                            continue;
                        }

                        const cloned = Cloner.Clone(this.globalProducts[globalIndex]);

                        cloned.quantity = checkFamilies[i].familyQuantity;

                        checkProducts.push(cloned);
                    }

                    this.manager.updateAllFamiliesPrices(checkProducts);

                    for(let i = 0, l = checkProducts.length; i < l; i++) {
                        const previousPrice = checkFamilies[i].promotion;

                        if(typeof previousPrice === "undefined") {
                            continue;
                        }

                        for(let j = 0, n = checkProducts[i].prices.compare!.length; j < n; j++) {
                            if(checkProducts[i].prices.compare![j].source !== "family") {
                                continue;
                            }

                            const familyPrice =
                                checkProducts[i].prices.compare![j].price /
                                checkProducts[i].prices.compare![j].quantity *
                                checkProducts[i].quantity;

                            totalPrice -= previousPrice;
                            totalPrice += familyPrice;

                            products[checkFamilies[i].index].promotion = familyPrice;

                            break;
                        }
                    }
                }

                result.uuids = uuids.concat(branches[i][j].uuids);
                result.promotions = promotion.concat(branches[i][j].promotions);
                result.products = products.concat(branches[i][j].products);
                result.reduction = reduction + branches[i][j].reduction;
                result.quantity = productQuantity + branches[i][j].quantity;
                result.total_price = totalPrice + branches[i][j].total_price;
                result.used_quantities = quantities;
                result.minimumQuantity = minimumQuantities.concat(branches[i][j].minimumQuantity);

                if(this.promotion.mix_criteria) {
                    result.total_percent = currentPercent + (branches[i][j].total_percent as number);

                    if((result.total_percent as number) > maxPercent) {
                        ObjectPool.Release(result);
                        continue;
                    }
                }

                results.push(result);
            }
        }

        return results;
    }

    /**
     * Creates a powerset from criteria
     * @param criteria
     * @returns Array
     * Solution from https://stackoverflow.com/a/18223072
     */
    protected static criteriaSubset(
        criteria: Array<PromotionCalculationPromotionCriteria>
    ): Array<Array<PromotionCalculationPromotionCriteria>> {
        const results = ArrayPool.Get<Array<PromotionCalculationPromotionCriteria>>();

        for(let mask = 0, total = Math.pow(2, criteria.length); mask < total; mask++) {
            const result = ArrayPool.Get<PromotionCalculationPromotionCriteria>();
            let i = criteria.length - 1;

            do {
                if((mask & (1 << i)) !== 0) {
                    result.push(criteria[i]);
                }
            } while(i--);

            if(result.length > 0) {
                results.push(result);
            }
        }

        return results;
    }

    /**
     * Run the promotion calculation
     */
    public async run(
        originalPromotion: null | string = null,
        branchDepth = 0
    ): Promise<PromotionCalculationResult> {
        // Check that there are any products
        if(!this.products.length) {
            return false;
        }

        // Get the promotional products
        let {
            promoProducts,
            productQuantity,
            basePrice,
            affectedProducts,
        } = this.getPromotionProducts(this.products);

        // Ensure we have promotional products
        if(!promoProducts || affectedProducts.length === 0) {
            // Release the promo products
            ArrayPool.Release(promoProducts);
            ArrayPool.Release(affectedProducts);

            // Return false
            return false;
        }

        let completePercent = 0;

        if(typeof this.promotion.complete_percent !== "undefined") {
            completePercent = this.promotion.complete_percent;
        }

        // Determine if the promotion is valid
        let {
            valid,
            maxMultiplication,
            minQuantity,
        } = this.checkPromotionIsValid(promoProducts, productQuantity, completePercent);

        if(!valid || maxMultiplication === 0) {
            // Release the promotion products
            ArrayPool.Release(promoProducts);
            ArrayPool.Release(affectedProducts);

            return false;
        }

        // Ensure we haven't exceeded the cap for the promotion on the sale
        if(this.promotion.sale_max_application_count) {
            maxMultiplication = Math.min(maxMultiplication, this.promotion.sale_max_application_count);
        }

        // Get the previous layers max_multiplication
        if(typeof this.promotion.max_multiplication !== "undefined") {
            maxMultiplication = this.promotion.max_multiplication;
        }

        // Get the original promotion's uuid (to stop infinite recursion for optional criteria)
        let originalId = originalPromotion;

        if(!originalId) {
            originalId = this.promotion.uuid;
        }

        // Prepare the required criteria with all the combinations of optional criteria added
        const optionalPromotions = ArrayPool.Get<PromotionCalculationPromotion>();
        let currentPercent = 0;

        if(!this.promotion.required_only && !this.requiredOnly && !this.settings.cross_promotion_count) {
            // Check how this promotion should be calculated
            if(this.promotion.criteria.length === 1) {
                // Bottom branch so just calculate
                this.promotion.criteria[0].optional = false;
                this.promotion.optional_only = false;
                this.promotion.required_only = true;

                if(this.promotion.mix_criteria) {
                    currentPercent += this.promotion.criteria[0].mix_percentage as number;
                }
            } else {
                // Calculate the first item if required
                if(this.promotion.calculate_only) {
                    this.promotion.criteria[0].optional = false;
                    this.promotion.optional_only = false;
                    this.promotion.required_only = true;
                }

                const optionalCriteria = ArrayPool.Get<PromotionCalculationPromotionCriteria>();
                const requiredCriteria = ArrayPool.Get<PromotionCalculationPromotionCriteria>();
                let maxPercent = 0;
                const optionalProducts = ArrayPool.Get();
                const requiredProducts = ArrayPool.Get<Record<string, PromotionCalculationProduct>>();

                // Sort the required and optional into separate arrays
                for(const i in this.promotion.criteria) {
                    if(!Object.prototype.hasOwnProperty.call(this.promotion.criteria, i)) continue;

                    if(this.promotion.criteria[i].invalid) continue;

                    if(this.promotion.criteria[i].optional) {
                        // Add to optional array
                        optionalProducts.push(promoProducts[i]);
                        optionalCriteria.push(this.promotion.criteria[i]);

                        // Add up maximum percent for next layer
                        if(this.promotion.mix_criteria) {
                            maxPercent += this.promotion.criteria[i].mix_percentage as number;
                        }
                    } else {
                        // Add the current layers percent
                        if(this.promotion.mix_criteria) {
                            currentPercent += this.promotion.criteria[i].mix_percentage as number;
                        }

                        // Add to array
                        requiredProducts.push(promoProducts[i]);
                        requiredCriteria.push(this.promotion.criteria[i]);
                    }
                }

                // Calculate the powerset for the current optionalCriteria
                if(optionalCriteria.length) {
                    let branchCriteria;

                    // Create the branches
                    if(!this.promotion.calculate_only) {
                        branchCriteria = PromotionCalculation.criteriaSubset(optionalCriteria);
                    } else {
                        branchCriteria = ArrayPool.Get<Array<PromotionCalculationPromotionCriteria>>();
                        branchCriteria.push(optionalCriteria);
                    }

                    // Prepare the branches
                    for(const j in branchCriteria) {
                        if(!Object.prototype.hasOwnProperty.call(branchCriteria, j)) continue;

                        const optionalPromotion = Cloner.Clone(this.promotion);

                        if(this.promotion.mix_criteria) {
                            optionalPromotion.complete_percent = 0;
                            optionalPromotion.max_multiplication = maxMultiplication;
                            optionalPromotion.max_percent = maxPercent;

                            // Calculate completePercent
                            for(const c in branchCriteria[j]) {
                                if(!Object.prototype.hasOwnProperty.call(branchCriteria[j], c)) continue;

                                optionalPromotion.complete_percent += branchCriteria[j][c].mix_percentage as number;
                            }

                            // Check if it is first layer of tree
                            if(!this.promotion.calculate_only) {
                                // Remove all branches that don't sum to a whole percentage
                                if(optionalPromotion.complete_percent % 1 !== 0) {
                                    ObjectPool.Release(optionalPromotion);
                                    continue;
                                }
                            }
                        }

                        optionalPromotion.optional_only = true;
                        optionalPromotion.required_only = false;
                        optionalPromotion.calculate_only = true;
                        optionalPromotion.criteria = branchCriteria[j];

                        optionalPromotions.push(optionalPromotion);
                    }
                }

                // Apply the new layer's criteria
                promoProducts = requiredProducts;
                this.promotion.criteria = requiredCriteria;
            }
        }

        this.maxMultiplication = maxMultiplication;

        // Get the sorted product order provided from the promotion manager
        const productOrder = this.globalProducts.map(product => product.uuid);

        // Use the promotion
        const usedQuantities = this.runPromotion(
            promoProducts,
            maxMultiplication,
            branchDepth !== 0,
            productOrder
        );

        if(this.promotionIsInvalid) {
            // Promotion isn't valid, exit early
            return false;
        }

        // if (!this.optionalOnly && !this.manager._settings.cross_promotion_count) {
        if(this.settings.cross_promotion_count) {
            // Assign the criteria to the products
            promoProducts = this.assignCriteriaToProducts(
                promoProducts as Array<Record<string, Required<PromotionCalculationProduct>>>,
                productQuantity,
                basePrice
            );
        }

        // Update the products pricing to be the promotional pricing
        const reduction = this.updateProductPricesFromPromotion(promoProducts, this.products);

        // Check if we are using cross promotion count or not.
        // If we are, we need to do the comparison within the promotion by using
        // a "branching" approach in order to determine which criteria and promotions work best with each
        // other.
        let results;

        if(!this.settings.cross_promotion_count) {
            // Calculate total quantities
            const prodQty = ObjectPool.Get() as Record<string, number>;

            for(const i in this.products) {
                if(!Object.prototype.hasOwnProperty.call(this.products, i)) continue;

                if(!prodQty[this.products[i].uuid]) {
                    prodQty[this.products[i].uuid] = 0;
                }

                prodQty[this.products[i].uuid] += this.products[i].quantity;
            }

            // Get the set max percent
            let maxPercent = maxMultiplication;

            if(this.promotion.max_percent) {
                maxPercent = this.promotion.max_percent;
            }

            // Check all possibilities with leftover products
            const branches = await this.checkAdditionalProducts(
                usedQuantities,
                prodQty,
                optionalPromotions,
                originalId,
                branchDepth
            );

            // Prepare results
            results = this.prepareBranches(
                promoProducts,
                prodQty,
                reduction,
                productQuantity,
                usedQuantities,
                branches,
                currentPercent,
                maxPercent,
                minQuantity,
                affectedProducts
            );
        } else {
            results = ObjectPool.Get() as PromotionCalculationResultSingle;
            results.uuid = this.promotion.uuid;
            results.promotion = this.promotion;
            results.products = this.products;
            results.reduction = reduction;
            results.quantity = productQuantity;
            results.minimumQuantity = minQuantity;
        }

        // Release all the variables used
        ArrayPool.Release(promoProducts);
        ArrayPool.Release(affectedProducts);

        // Store in the cache
        AdvancedPromotionCache.storeResult(
            this.promotion,
            this.products,
            results
        );

        // All done
        return results;
    }
}

